// @flow
import * as React from 'react';

import { useDispatch } from 'react-redux';

import { selectHasDomainCorporateProducts } from '@gandi/cart';
import { useTranslator } from '@gandi/translate';
import { Base, Heading, HeadingIcon, Text } from '@gandi/ui';

import { useAppSelector } from 'src/hooks/useAppSelector';

import type { Query } from '../../../DomainCreate/components/DomainSuggest';
import type { Params } from '../../../helpers/withRouter';
import { selectShopCurrent } from '../../../modules/organization/selectors';
import type {
  ComponentWithPrefetch,
  FullState,
  PreFetchArgs,
  PreFetchReturn,
} from '../../../types';

import {
  corporateResource,
  fetchOneCorpoPack,
  selectCorpoPack,
} from '../../modules/corporate';

import styles from './CorporateAlert.scss';

function CorporateAlert(): React.MixedElement | null {
  const { __, formatDate } = useTranslator();
  const dispatch = useDispatch();
  const currentOrg = useAppSelector(state => selectShopCurrent(state, true));
  const corporateProduct = useAppSelector(selectHasDomainCorporateProducts);
  const sharingId = currentOrg?.id;

  const corpoPack = useAppSelector(state => selectCorpoPack(state, sharingId));
  const date = corpoPack?.[0]?.date_end;

  const corporate = currentOrg?.corporate && corporateProduct;
  const shoulFetchCorpoPack = useAppSelector(state =>
    corporateResource.selectShouldFetchOne(state, sharingId),
  );

  React.useEffect(() => {
    if (corporate && shoulFetchCorpoPack) {
      dispatch(fetchOneCorpoPack(currentOrg.id));
    }
  }, [corporate, currentOrg?.id, dispatch, shoulFetchCorpoPack]);

  if (!corporateProduct || !currentOrg) return null;

  if (currentOrg.corporate) {
    return (
      <Base padding="small" backgroundColor="surface" marginBottom="base">
        <Heading fontSize="s" marginBottom="small">
          <HeadingIcon
            name="badgeCorporate"
            slot="iconStart"
            className={styles.badge}
          />
          {__(
            'This organization has an active Corporate Pack until %(date)s.',
            {
              date: date ? formatDate(date, 'LL') : __('Unkown date'),
            },
          )}
        </Heading>
        <Text display="block">
          {__(
            'Your cart contains domain names with extensions reserved for Corporate services members.',
          )}
        </Text>
        <Text display="block">
          {__(
            'You can register as many domains as you want during this period.',
          )}
        </Text>
        <Text display="block">
          {__(
            'Any domain names you aquire remains yours and can always be renewed, even after the ' +
              'temporary subscription expires.',
          )}
        </Text>
      </Base>
    );
  }

  return (
    <Base padding="small" backgroundColor="surface" marginBottom="base">
      <Heading fontSize="s" marginBottom="small">
        <HeadingIcon name="badgeCorporate" slot="iconStart" />
        {__(
          'Your cart contains domain names with extensions reserved for Corporate services members.',
        )}
      </Heading>
      <Text display="block">
        {__(
          'A Corporate Pack is added to your cart to allow you to buy them. You will define ' +
            'the desired subscription length in the next step. You can register as many domains as ' +
            'you want during this period.',
        )}
      </Text>
      <Text display="block">
        {__(
          'Any domain names you aquire remain yours and can always be renewed, even after the ' +
            'temporary subscription expires.',
        )}
      </Text>
    </Base>
  );
}

CorporateAlert.preFetch = (
  args: PreFetchArgs<{}, Params, Query, FullState>,
): PreFetchReturn<> => {
  const { dispatch, getState } = args;

  const corporateProduct = selectHasDomainCorporateProducts(getState());
  const org = selectShopCurrent(getState(), true);
  const corporate = org?.corporate && corporateProduct;

  return corporate ? dispatch(fetchOneCorpoPack(org.id)) : Promise.resolve();
};

type CorporateAlertType = ComponentWithPrefetch<
  {},
  typeof CorporateAlert.preFetch,
>;

export default (CorporateAlert: CorporateAlertType);
